import type { ValidationReportItem } from '@/domains/report/api/types';

import type { OverviewBlock, OverviewBlockWidth } from '../api/types';

export type OverviewStatus = 'present' | 'inconsistent' | 'missing';

export type ResolvedCriterion = {
  item: ValidationReportItem;
  /** Criterion.type uit de database; null = geen type vastgelegd. */
  type: string | null;
};

export type ResolvedBlock = {
  key: string;
  title: string;
  width: OverviewBlockWidth;
  status: OverviewStatus;
  criteria: ResolvedCriterion[];
  /** Twee datetime-criteria → weergave als periode (van → tot). */
  isRange: boolean;
};

export function blockStatus(items: ValidationReportItem[]): OverviewStatus {
  if (items.some((i) => i.consistent === false)) return 'inconsistent';
  if (items.some((i) => i.answers.length > 0)) return 'present';
  return 'missing';
}

/**
 * Koppelt een configblok aan de criteria in het rapport van het actieve
 * perspectief. Onbekende codes (typefout, of niet in dit perspectief) worden
 * overgeslagen met een waarschuwing; zonder één bekende code vervalt het blok.
 */
export function resolveBlock(
  block: OverviewBlock,
  index: number,
  itemsByCode: ReadonlyMap<string, ValidationReportItem>,
  typeByCode: ReadonlyMap<string, string | null>,
): ResolvedBlock | null {
  const criteria: ResolvedCriterion[] = [];
  const unknown: string[] = [];

  for (const code of block.criteria) {
    const item = itemsByCode.get(code);
    if (!item) {
      unknown.push(code);
      continue;
    }
    criteria.push({ item, type: typeByCode.get(code) ?? null });
  }

  if (unknown.length > 0) {
    console.warn('[overview] onbekende criterium-codes in blok overgeslagen', {
      blockIndex: index,
      codes: unknown,
    });
  }

  if (criteria.length === 0) return null;

  return {
    key: `${index}-${block.criteria.join(',')}`,
    title: block.title?.trim() || criteria.map((c) => c.item.name).join(' + '),
    width: block.width,
    status: blockStatus(criteria.map((c) => c.item)),
    criteria,
    isRange: criteria.length === 2 && criteria.every((c) => c.type === 'datetime'),
  };
}
